import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import type { CSSProperties } from 'react';
import { isMapAvailable } from './map';

// Engineering grid backdrop for the map area, echoing the radar digital-twin
// look (dark canvas + faint grid lines). Shown where no basemap tiles render
// (provider unavailable, or while the boot probe is still resolving).

const GRID_BG = '#0a0f16';
const GRID_LINE = 'rgba(255, 255, 255, 0.18)';
const GRID_STEP = 32;

// CSS variant for the loading state, where no OpenLayers map exists yet. Fixed
// to the viewport (there is nothing to pan with at that point).
export const MAP_GRID_BG: CSSProperties = {
  backgroundColor: GRID_BG,
  backgroundImage:
    `linear-gradient(${GRID_LINE} 1px, transparent 1px),` +
    `linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: `${GRID_STEP}px ${GRID_STEP}px`,
};

// One opaque 256px grid tile, repeated by the tile layer below. Built lazily
// (needs the DOM canvas) and cached. 256 / 32 = 8 cells, so adjacent tiles align.
let gridTileUrl: string | null = null;
function gridTile(): string {
  if (gridTileUrl) return gridTileUrl;
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  // No 2d context (transient context-loss / OOM): return an undrawn tile without
  // caching so a later call can retry instead of memoizing a blank backdrop.
  if (!ctx) return canvas.toDataURL();
  ctx.fillStyle = GRID_BG;
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = GRID_LINE;
  ctx.lineWidth = 1;
  for (let p = 0; p <= size; p += GRID_STEP) {
    ctx.beginPath();
    ctx.moveTo(p + 0.5, 0);
    ctx.lineTo(p + 0.5, size);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, p + 0.5);
    ctx.lineTo(size, p + 0.5);
    ctx.stroke();
  }
  gridTileUrl = canvas.toDataURL();
  return gridTileUrl;
}

// Map layer rendering the grid as a repeating tile, so it pans with the view
// like the POI/digital-twin overlays above it. Visible only in the placeholder
// (provider-unavailable) state, since the opaque grid would otherwise hide a
// loaded basemap. Each call site inserts it after the dark-theme base layers
// (whose ol-ext invert filter would otherwise flip it) and before the overlays.
export function createMapGridLayer() {
  const url = gridTile();
  return new TileLayer({
    source: new XYZ({ tileUrlFunction: () => url }),
    visible: !isMapAvailable(),
  });
}
